import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

export type AssignmentStatus = 'planned' | 'active' | 'completed' | 'cancelled';

// empty_reason is a TEXT column driven by the picklist_value table.
// Use usePicklistValues('empty_reason') for dropdown options and bar labels.

export interface VesselAssignment {
  id: string;
  org_id: string;
  vessel_id: string;
  // Exactly one of wine_lot_id / planning_lot_id is non-null (DB CHECK constraint).
  wine_lot_id: string | null;
  planning_lot_id: string | null;
  fill_date: string;
  projected_empty_date: string | null;
  actual_empty_date: string | null;
  status: AssignmentStatus;
  fill_volume_l: number | null;
  empty_volume_l: number | null;
  notes: string | null;
  color: string | null;
  source_assignment_id: string | null;
  empty_reason: string | null;
  empty_reason_notes: string | null;
  display_label: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Shape of a joined planning_lot record on an assignment
export interface PlanningLotRef {
  id: string;
  code: string;
  varietal: string | null;
  vintage: number | null;
  current_volume_l: number | null;
  is_received: boolean;
  wine_lot_id: string | null;
  client: { id: string; name: string } | null;
}

export interface VesselAssignmentWithRelations extends VesselAssignment {
  vessel: {
    id: string;
    name: string;
    type: string;
    capacity_l: number | null;
  };
  // Present when wine_lot_id is set (real production lot)
  wine_lot: {
    id: string;
    code: string;
    varietal: string | null;
    vintage: number | null;
    client: { id: string; name: string } | null;
  } | null;
  // Present when planning_lot_id is set (planner-owned lot)
  planning_lot: PlanningLotRef | null;
  /**
   * Customer-portal Tank Schedule only (get_client_tank_schedule). false = another
   * client's bar, masked on the server and rendered as a grey "Occupied" block.
   * Undefined in the staff planner.
   */
  is_mine?: boolean;
}

// ============================================================================
// Normalized lot reference — use this in UI components instead of accessing
// wine_lot / planning_lot directly. Keeps display code lot-source-agnostic.
// ============================================================================

export interface LotRef {
  id: string;
  code: string;
  varietal: string | null;
  vintage: number | null;
  current_volume_l?: number | null;
  client: { id: string; name: string } | null;
  /** True when backed by a planning_lot that has not yet been marked received */
  is_tentative: boolean;
}

export function getLotForAssignment(
  assignment: VesselAssignmentWithRelations
): LotRef | null {
  if (assignment.wine_lot) {
    return {
      id: assignment.wine_lot.id,
      code: assignment.wine_lot.code,
      varietal: assignment.wine_lot.varietal,
      vintage: assignment.wine_lot.vintage,
      client: assignment.wine_lot.client,
      is_tentative: false,
    };
  }
  if (assignment.planning_lot) {
    return {
      id: assignment.planning_lot.id,
      code: assignment.planning_lot.code,
      varietal: assignment.planning_lot.varietal,
      vintage: assignment.planning_lot.vintage,
      current_volume_l: assignment.planning_lot.current_volume_l,
      client: assignment.planning_lot.client,
      is_tentative: !assignment.planning_lot.is_received,
    };
  }
  return null;
}

// ============================================================================
// Supabase select fragment — shared across all queries
// ============================================================================

const ASSIGNMENT_SELECT = `
  *,
  vessel:vessel_id (
    id,
    name,
    type,
    capacity_l
  ),
  wine_lot:wine_lot_id (
    id,
    code,
    varietal,
    vintage,
    client!wine_lot_client_id_fkey (
      id,
      name
    )
  ),
  planning_lot:planning_lot_id (
    id,
    code,
    varietal,
    vintage,
    current_volume_l,
    is_received,
    wine_lot_id,
    client:client_id (
      id,
      name
    )
  )
`;

// ============================================================================
// Input types
// ============================================================================

export interface CreateAssignmentInput {
  vessel_id: string | null;
  /** Set for real production lots */
  wine_lot_id?: string | null;
  /** Set for planner-owned lots */
  planning_lot_id?: string | null;
  fill_date: string;
  projected_empty_date?: string | null;
  fill_volume_l?: number | null;
  notes?: string;
  color?: string;
  source_assignment_id?: string | null;
  display_label?: string | null;
}

export interface UpdateAssignmentInput {
  fill_date?: string;
  projected_empty_date?: string | null;
  actual_empty_date?: string | null;
  vessel_id?: string | null;
  status?: AssignmentStatus;
  fill_volume_l?: number | null;
  empty_volume_l?: number | null;
  notes?: string | null;
  color?: string;
  empty_reason?: string | null;
  empty_reason_notes?: string | null;
  display_label?: string | null;
}

export interface ConflictInfo {
  conflicting_id: string;
  conflicting_lot_code: string;
  conflict_start: string;
  conflict_end: string | null;
}

// ============================================================================
// Query: Fetch assignments for date range
// ============================================================================

export function useVesselAssignments(startDate: Date, endDate: Date) {
  return useQuery({
    queryKey: ["vessel-assignments", startDate.toISOString().split('T')[0], endDate.toISOString().split('T')[0]],
    queryFn: async () => {
      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from("vessel_assignment")
        .select(ASSIGNMENT_SELECT)
        .in("status", ["planned", "active"])
        .not("vessel_id", "is", null)
        .lte("fill_date", endStr)
        .order("fill_date", { ascending: false });

      if (error) throw error;

      // Client-side filter for date range overlap
      const filtered = (data || []).filter((a) => {
        const emptyDate = a.actual_empty_date || a.projected_empty_date;
        if (!emptyDate) return true;
        return emptyDate >= startStr;
      });

      filtered.sort((a, b) => a.fill_date.localeCompare(b.fill_date));

      return filtered as VesselAssignmentWithRelations[];
    },
    staleTime: 60000,
    gcTime: 300000,
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Query: Fetch single assignment
// ============================================================================

export function useVesselAssignment(assignmentId: string | null) {
  return useQuery({
    queryKey: ["vessel-assignment", assignmentId],
    queryFn: async () => {
      if (!assignmentId) return null;

      const { data, error } = await supabase
        .from("vessel_assignment")
        .select(ASSIGNMENT_SELECT)
        .eq("id", assignmentId)
        .single();

      if (error) throw error;
      return data as VesselAssignmentWithRelations;
    },
    enabled: !!assignmentId,
  });
}

// ============================================================================
// Mutation: Create assignment
// ============================================================================

export function useCreateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAssignmentInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("vessel_assignment")
        .insert([{
          ...input,
          org_id: userData.org_id,
          created_by: userData.id,
          status: 'planned',
        }])
        .select(ASSIGNMENT_SELECT)
        .single();

      if (error) {
        if (error.code === '23P01') {
          throw new Error("This vessel is already occupied during the selected dates");
        }
        throw error;
      }
      return data as VesselAssignmentWithRelations;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["on-deck-assignments"] });
      toast.success("Assignment created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create assignment: ${error.message}`);
    },
  });
}

// ============================================================================
// Mutation: Update assignment
// ============================================================================

export function useUpdateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateAssignmentInput }) => {
      const { data, error } = await supabase
        .from("vessel_assignment")
        .update(input as any)
        .eq("id", id)
        .select(ASSIGNMENT_SELECT)
        .single();

      if (error) {
        if (error.code === '23P01') {
          throw new Error("Cannot move: vessel is occupied during these dates");
        }
        throw error;
      }
      return data as VesselAssignmentWithRelations;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["on-deck-assignments"] });
      toast.success("Assignment updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// ============================================================================
// Mutation: Delete assignment
// ============================================================================

export function useDeleteAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("vessel_assignment")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["on-deck-assignments"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });
}

// ============================================================================
// Mutation: Duplicate lot to On Deck
// ============================================================================

export function useDuplicateLotToOnDeck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignment: VesselAssignmentWithRelations) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      // Forward whichever lot FK is set on the source assignment
      const lotRef = assignment.wine_lot_id
        ? { wine_lot_id: assignment.wine_lot_id }
        : { planning_lot_id: assignment.planning_lot_id };

      const { error } = await supabase
        .from("vessel_assignment")
        .insert([{
          vessel_id: null,
          ...lotRef,
          fill_date: assignment.fill_date,
          projected_empty_date: assignment.projected_empty_date,
          fill_volume_l: assignment.fill_volume_l,
          display_label: assignment.display_label,
          org_id: userData.org_id,
          created_by: userData.id,
          status: 'planned',
        }]);

      if (error) throw error;
    },
    onSuccess: (_, assignment) => {
      queryClient.invalidateQueries({ queryKey: ["on-deck-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
      const lot = getLotForAssignment(assignment);
      toast.success(`${lot?.code ?? 'Lot'} duplicated to On Deck`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// ============================================================================
// Helper: Check for conflicts
// ============================================================================

export async function checkAssignmentConflict(
  vesselId: string,
  fillDate: string,
  emptyDate: string | null,
  excludeId?: string
): Promise<ConflictInfo[]> {
  const { data, error } = await supabase.rpc("check_assignment_conflict", {
    p_vessel_id: vesselId,
    p_fill_date: fillDate,
    p_empty_date: emptyDate,
    p_exclude_id: excludeId || null,
  });

  if (error) {
    console.error("Error checking conflicts:", error);
    return [];
  }
  return data || [];
}

// ============================================================================
// Types: Transfer Connections
// ============================================================================

export interface TransferConnection {
  connection_id: string;           // assignment_connection.id — used for delete
  source_assignment_id: string;
  source_vessel_id: string;
  source_fill_date: string;
  source_empty_date: string | null;
  target_assignment_id: string;
  target_vessel_id: string;
  target_fill_date: string;
  color: string | null;
  varietal: string | null;
  lot_code: string | null;         // source lot code — used for VV-first color lookup
}

// ============================================================================
// Helper: Compute transfer connections from junction rows + in-memory assignments
// ============================================================================

export function computeTransferConnections(
  assignments: VesselAssignmentWithRelations[],
  connectionRows: { id: string; source_id: string; target_id: string }[]
): TransferConnection[] {
  const assignmentMap = new Map(assignments.map((a) => [a.id, a]));

  return connectionRows.flatMap((conn) => {
    const source = assignmentMap.get(conn.source_id);
    const target = assignmentMap.get(conn.target_id);
    if (!source || !target) return [];

    const sourceLot = getLotForAssignment(source);
    return [{
      connection_id: conn.id,
      source_assignment_id: source.id,
      source_vessel_id: source.vessel_id,
      source_fill_date: source.fill_date,
      source_empty_date: source.projected_empty_date || source.actual_empty_date,
      target_assignment_id: target.id,
      target_vessel_id: target.vessel_id,
      target_fill_date: target.fill_date,
      color: source.color,
      varietal: sourceLot?.varietal ?? null,
      lot_code: sourceLot?.code ?? null,
    }];
  });
}

// ============================================================================
// Mutation: Create scheduled transfer (copy/paste)
// ============================================================================

export interface CreateScheduledTransferInput {
  source_assignment_id: string;
  target_vessel_id: string;
  scheduled_date: string;
  projected_empty_date?: string | null;
  wine_lot_id?: string | null;
  planning_lot_id?: string | null;
  fill_volume_l?: number | null;
  notes?: string;
  color?: string;
}

export function useCreateScheduledTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateScheduledTransferInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("vessel_assignment")
        .insert([{
          vessel_id: input.target_vessel_id,
          wine_lot_id: input.wine_lot_id ?? null,
          planning_lot_id: input.planning_lot_id ?? null,
          fill_date: input.scheduled_date,
          projected_empty_date: input.projected_empty_date || null,
          fill_volume_l: input.fill_volume_l,
          notes: input.notes,
          color: input.color,
          source_assignment_id: input.source_assignment_id,
          org_id: userData.org_id,
          created_by: userData.id,
          status: 'planned',
        }])
        .select(ASSIGNMENT_SELECT)
        .single();

      if (error) {
        if (error.code === '23P01') {
          throw new Error("This vessel is already occupied during the selected dates");
        }
        throw error;
      }
      return data as VesselAssignmentWithRelations;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
      toast.success("Transfer scheduled successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to schedule transfer: ${error.message}`);
    },
  });
}
